import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    'Income Tax Filing',
    'GST Filing',
    'Business Registration',
    'Tax Planning',
    'Audit Services',
    'Compliance',
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Instagram, href: '#', label: 'Instagram' },
  ];

  return (
    <footer 
      data-testid="footer"
      className="bg-navy-950 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div data-testid="footer-company-info" className="space-y-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">T</span>
              </div>
              <span className="font-heading font-bold text-xl text-white">
                Tax<span className="text-blue-400">Company</span>
              </span>
            </Link>
            <p className="text-slate-400 leading-relaxed">
              Your trusted partner for all tax and compliance needs. We help individuals and businesses navigate the complex world of taxation.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  data-testid={`social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 rounded-full bg-navy-800 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-blue-500 hover:text-white hover:border-blue-500 transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div data-testid="footer-quick-links">
            <h4 className="font-heading font-semibold text-white text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-slate-400 hover:text-blue-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div data-testid="footer-services">
            <h4 className="font-heading font-semibold text-white text-lg mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-slate-400">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div data-testid="footer-contact-info">
            <h4 className="font-heading font-semibold text-white text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                <span className="text-slate-400">
                  B-248 2nd Floor North Ex Mall Rohini Sector 9 Delhi 110085
                </span>
              </li>
              <li>
                <a 
                  href="tel:+919717705640" 
                  className="flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors"
                >
                  <Phone className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  +91-9717705640
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@taxcompany.in" 
                  className="flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors"
                >
                  <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  info@taxcompany.in
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p data-testid="footer-copyright" className="text-slate-500 text-sm">
              {currentYear} TaxCompany. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-slate-500">
              <a href="#" className="hover:text-blue-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-blue-400 transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
